import React from "react";
import { Modal } from "../../components";
import { MainPanelOrSectionPanel } from "../../components/panels";
import { type IPagination, Pagination } from "../../pagination";
import NoteCreateModal, { type NoteState } from "../create/app";
import NoteRowCard from "./NoteRow";
import { useShortcutPanel } from "components/Shortcuts/hooks";
import { ButtonSecondary } from "components/Atoms/Button";
interface Props {
  mainArea: server.dto.MainAreas
  config: server.dto.LinkedNotePanelConfig
  useSectionPanel?: boolean
}

const NotesPanel = (props: Props) => {
  const getDefaultNoteState = (): NoteState => ({
    charactersRemaining: serverReference.constants.note.maxBodyLength,
    visibleToExternal: props.config.defaultVisibleToExternal,
    subject: "",
    body: "",
    subjects: [],
    noSubjectError: false,
    bodyTooLongError: false,
    isSaving: false,
    noteError: null // We can use this in the future to show API errors
  });

  const [notes, setNotes] = React.useState<server.dto.Note[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [editingNote, setEditingNote] = React.useState<server.dto.Note>(undefined);
  const [pagination, setPagination] = React.useState<IPagination>({
    total: 0,
    page: 1,
    perPage: 10,
    maxPerPage: 10,
    pageCount: 0
  });

  const [noteState, setNoteState] = React.useState<NoteState>(getDefaultNoteState());

  const updateNoteState = (partial: Partial<NoteState>) => {
    setNoteState(prevNoteState => ({
      ...prevNoteState,
      ...partial
    }));
  };

  const reloadNotes = async (page = 1) => {
    setLoading(true);
    setPagination({ ...pagination, page });
    // const result = await noteApi.GetNotes(props.config.itemId, page, pagination.perPage);
    const result = await window.CoreApi.Note.List({
      mainArea: props.mainArea,
      page,
      perPage: pagination.perPage,
      parentId: props.config.itemId
    });
    setPagination(result.data.pagination);
    setNotes(result.data.data);
    setLoading(false);
  };

  window.refreshNotes = reloadNotes;

  const onFinishedSaving = async () => {
    setEditingNote(undefined);
    setNoteState(getDefaultNoteState());
    return reloadNotes();
  };

  const onNoteEdit = (note: server.dto.Note) => {
    let newNoteState = getDefaultNoteState();
    newNoteState = {
      ...newNoteState,
      visibleToExternal: note.visibleToExternal,
      subject: note.title || "",
      body: note.body || ""
    };
    setEditingNote(note);
    setNoteState(newNoteState);
  };

  const onClose = () => {
    if (editingNote) {
      setNoteState(getDefaultNoteState());
    }
    setEditingNote(undefined);
  };

  const { refToScrollTo } = useShortcutPanel({
    type: "note",
    label: props.config.headerLabel,
    totalItems: notes.length,
    onAddClicked: props.config.canAdd ? () => { setEditingNote(null); } : undefined
  });

  return <>
    <MainPanelOrSectionPanel
      useSectionPanel={props.useSectionPanel}
      title={props.config.headerLabel}
      isLoading={loading}
      hasData={notes.length > 0}
      action={props.config.canAdd && <ButtonSecondary onClick={() => { setEditingNote(null); }} label={RESX.Buttons.btnNew} />}
      panelRef={refToScrollTo}
      pagination={<Pagination
        paginationData={pagination}
        onPaginate={reloadNotes}
      />}
      legacyPanelProps={{
        panelClassName: "s1_related-feature-panel"
      }}
    >
      <div className="list-with-gaps line-layout">
        <div className="list-with-gaps-body">
          {notes.map(note => <NoteRowCard
            key={note.noteId}
            note={note}
            onEdit={onNoteEdit}
            onDeleted={reloadNotes}
            canDelete={note.permissions.canDelete}
            canEdit={note.permissions.canModify}
            config={props.config}
          />)}
        </div>
      </div>
    </MainPanelOrSectionPanel>
    <Modal
      title={editingNote === null ? RESX.Note.NewNote : RESX.Message.EditNote}
      handleClose={onClose}
      width="large"
      isOpen={editingNote !== undefined}
      hasAutoScrollBox
    >
      <NoteCreateModal
        mainArea={props.mainArea}
        config={props.config}
        existingNote={editingNote}
        onFinishedSaving={onFinishedSaving}
        noteState={noteState}
        onNoteStateChanged={updateNoteState}
      />
    </Modal>
  </>;
};

export default NotesPanel;